// format-cols.jsx — column factories so a new grid column inherits the
// AGREED FORMATS (format.js) by construction, instead of by remembering to
// (NIGHT_SHIFT.md item 6, STATUS.md 2026-07-19). Must be LESS typing than
// hand-writing the column, or it will not get used.
//
// Empty-value states (STATUS.md, settled 2026-07-19) — three, and the third
// is the point:
//   not applicable      -> wrap the returned column in naCol() (App.jsx) — this
//                           factory doesn't know about cost_class, so it isn't
//                           its job to decide #NA.
//   empty, and that's fine -> a muted DASH
//   empty, but REQUIRED  -> a WARN, in shape AND word (never colour alone)
// `required` is a predicate over the ROW, not a property of the value —
// pass it only where requiredness genuinely exists today (Alex: RM Source /
// FabLabour on fabricated lines). Omit it everywhere else; do not invent
// requiredness a column didn't have before.

import MoneyCell from './MoneyCell.jsx';
import { percent, multiplierToPercent, qty, labor_productivity } from './format.js';
import { WARN, DASH, FORMULA } from './icons.js';

function emptyMark(row, required) {
  const isRequired = typeof required === 'function' ? !!required(row) : false;
  if (isRequired) {
    return <span style={{ color: 'var(--th-status-deprecated)', fontSize: 'var(--fs-small)' }}>{WARN} required</span>;
  }
  return <span style={{ color: 'var(--th-text-faint)', fontSize: 'var(--fs-small)' }}>{DASH}</span>;
}

// moneyCol — a plain $ cell. Reuses MoneyCell (the existing, already-correct
// $-hard-left / compact-large-value component) so a money column is one call
// instead of a hand-written render.
export function moneyCol({ key, label, width, required, bold, ...rest }) {
  return {
    key, label, width, align: 'right', editable: true,
    render: row => row[key] != null ? <MoneyCell n={row[key]} bold={bold} /> : emptyMark(row, required),
    ...rest,
  };
}

// percentCol — stored multiplier, shown/typed as %. `defaultValue` is the
// assumed multiplier when the field is null (e.g. net_to_gross defaults to 1 =
// no waste) — pass it only where that assumption already exists; omit it to
// get the plain empty-state instead. `title` may be a string or (row) =>
// string for a row-aware tooltip.
export function percentCol({ key, label, width = 110, defaultValue, title, required, ...rest }) {
  return {
    key, label, width, align: 'right', editable: true,
    editValue: row => {
      if (row[key] != null) return multiplierToPercent(row[key]);
      return defaultValue != null ? multiplierToPercent(defaultValue) : null;
    },
    render: row => {
      if (row[key] == null && defaultValue == null) return emptyMark(row, required);
      const v = row[key] != null ? row[key] : defaultValue;
      return (
        <span
          title={typeof title === 'function' ? title(row) : title}
          style={{ fontFamily: 'var(--ff-mono)', fontSize: 'var(--fs-mono)', color: 'var(--th-navy)' }}
        >
          {percent(v)}
        </span>
      );
    },
    ...rest,
  };
}

// qtyCol — comma thousands, 0 decimals. `unit` is a string or (row) => string;
// defaults to the row's own `unit` field when present.
export function qtyCol({ key, label, width, unit, required, ...rest }) {
  return {
    key, label, width, align: 'right', editable: true,
    render: row => {
      if (row[key] == null) return emptyMark(row, required);
      const u = typeof unit === 'function' ? unit(row) : (unit ?? row.unit);
      return <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 'var(--fs-small)' }}>{qty(row[key], u)}</span>;
    },
    ...rest,
  };
}

// productivityCol — comma thousands, 1 decimal, ALWAYS (a TYPE — output_rate —
// never a magnitude threshold). `unit` is a string or (row) => string;
// defaults to "<row.unit>/hr" so the direction reads unambiguously (Alex:
// higher output_rate = fewer hours = cheaper, the opposite of net_to_gross).
export function productivityCol({ key, label, width, unit, required, ...rest }) {
  return {
    key, label, width, align: 'right', editable: true,
    render: row => {
      if (row[key] == null) return emptyMark(row, required);
      const u = typeof unit === 'function' ? unit(row) : (unit ?? (row.unit ? `${row.unit}/hr` : 'hr'));
      return <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 'var(--fs-small)' }}>{labor_productivity(row[key], u)}</span>;
    },
    ...rest,
  };
}

// formulaCol — NIGHT_SHIFT.md item 4 (2026-07-19): a numeric cell that ALSO
// accepts a FORMULA and keeps it. `key` is the *_expr TEXT column
// (net_to_gross_expr / price_expr) — editing/staging keys off THIS field
// (editType:'formula' routes the input through FormulaCell, see DataGrid.jsx
// — it already renders the ƒ marker and the syntax-help popover). `valueKey`
// is the priced numeric column the expression derives (net_to_gross / price);
// evaluating it and storing BOTH is the server's job (catalog-db.js
// updateLine, reusing evalFormula from src/formula.js — the SAME evaluator
// product-config's qty_expr/size_expr use, no second syntax) — this factory
// only renders and wires the edit control.
//
// A row with no expression yet (every pre-item-4 row, and any row entered as
// a plain number) renders IDENTICAL to the plain percentCol/moneyCol cell it
// replaces — zero visual regression. Once an expression exists, the cell
// shows it with the resolved value MUTED beside it ("360/480 = 75%"), so the
// formula's origin is never lost the way a bare "0.75" would lose it.
// `suffix` (NIGHT_SHIFT.md item 5) is a short muted WORD after the value, for a
// column whose DIRECTION is not self-evident. A bare "72.9%" does not say
// whether up is better; "72.9% of standard" does. Same job the unit does in
// productivityCol ("13.2 kg/hr" reads unambiguously, "13.2" does not) — three
// numbers in this area run in two different directions (net_to_gross up = dearer,
// output_rate up = cheaper, labor_productivity up = cheaper) and that cost Alex time.
export function formulaCol({ key, valueKey, label, width, kind, title, suffix, required, ...rest }) {
  const valueNode = (row) => kind === 'percent'
    ? <span style={{ fontFamily: 'var(--ff-mono)', fontSize: 'var(--fs-mono)', color: 'var(--th-navy)' }}>{percent(row[valueKey])}</span>
    // kind:money is only used for the catalog LINE PRICE, which is a unit price -> cents.
    : <MoneyCell n={row[valueKey]} unit />;
  const suffixNode = suffix
    ? <span style={{ color: 'var(--th-text-secondary)', fontSize: 'var(--fs-micro)' }}>{suffix}</span>
    : null;
  return {
    key, label, width, align: 'right',
    editable: true, editType: 'formula',
    editValue: row => row[key] ?? (row[valueKey] != null ? String(row[valueKey]) : ''),
    render: row => {
      const resolvedTitle = typeof title === 'function' ? title(row) : title;
      const expr = row[key];
      if (!expr) {
        if (row[valueKey] == null) return emptyMark(row, required);
        return (
          <span title={resolvedTitle} style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4 }}>
            {valueNode(row)}
            {suffixNode}
          </span>
        );
      }
      // READ MODE SHOWS THE RESULT ONLY (Alex, 2026-07-23, on seeing
      // "1500 = $1,500.00"): "even if you keep the formula in the cell, on the
      // visu we should see the result only. Maybe the F sign if there is a
      // formula inside."
      //
      // Two cases, and separating them is the point:
      //   a BARE NUMBER typed into the cell is still stored as an expression
      //   ("1500"), and echoing it back adds nothing — that renders EXACTLY
      //   like a plain money/percent cell, no marker at all.
      //   a REAL formula ("2*1000") shows the resolved value with a muted ƒ
      //   in front; the expression itself moves to the tooltip and is still
      //   fully editable (FormulaCell owns edit mode).
      const raw = String(expr).trim();
      const isRealFormula = raw !== '' && !/^-?\d+(?:\.\d+)?$/.test(raw);
      if (!isRealFormula) {
        return (
          <span title={resolvedTitle} style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4 }}>
            {valueNode(row)}
            {suffixNode}
          </span>
        );
      }
      return (
        <span
          title={resolvedTitle ? `${raw}\n${resolvedTitle}` : raw}
          style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4 }}
        >
          <span style={{ fontFamily: 'var(--ff-mono)', fontSize: 'var(--fs-mono)', color: 'var(--th-text-secondary)' }}>{FORMULA}</span>
          {valueNode(row)}
          {suffixNode}
        </span>
      );
    },
    ...rest,
  };
}
